import React, { useState } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { BellRing, Edit2, Plus } from 'lucide-react';
import { toast } from 'sonner';

export const NextServiceNote = ({ customerId, note, onSaved, editable = true }) => {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState('');
  const [saving, setSaving] = useState(false);

  const openEditor = () => { setValue(note || ''); setOpen(true); };

  const save = async (text) => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/customers/${customerId}/next-service-note`, { note: text });
      onSaved?.(res.data.next_service_note);
      toast.success(res.data.next_service_note ? 'Merknad lagret' : 'Merknad fjernet');
      setOpen(false);
    } catch {
      toast.error('Kunne ikke lagre merknad');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div onClick={(e) => e.stopPropagation()}>
      {note ? (
        <div className="flex items-start gap-2 p-2.5 rounded-lg bg-amber-50 border border-amber-300 text-amber-900" data-testid={`next-note-${customerId}`}>
          <BellRing className="h-4 w-4 mt-0.5 shrink-0 text-amber-600" />
          <div className="min-w-0 flex-1 text-sm">
            <span className="font-semibold">Til neste service: </span>
            <span className="whitespace-pre-wrap">{note}</span>
          </div>
          {editable && (
            <Button type="button" size="sm" variant="ghost" className="h-7 w-7 p-0 text-amber-700 hover:bg-amber-100" onClick={openEditor} data-testid={`next-note-edit-${customerId}`}>
              <Edit2 className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
      ) : editable ? (
        <Button type="button" size="sm" variant="ghost" className="h-7 px-2 text-xs text-slate-500 hover:text-amber-700" onClick={openEditor} data-testid={`next-note-add-${customerId}`}>
          <Plus className="h-3.5 w-3.5 mr-1" />Merknad til neste service
        </Button>
      ) : null}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Merknad til neste service</DialogTitle>
            <DialogDescription>Vises automatisk ved neste rens/service, f.eks. «Bytt fatkobling».</DialogDescription>
          </DialogHeader>
          <Textarea value={value} onChange={(e) => setValue(e.target.value)} rows={3} maxLength={500} placeholder="F.eks. Bytt fatkobling ved neste rens" data-testid="next-note-textarea" autoFocus />
          <div className="flex gap-2 justify-end">
            {note && (
              <Button type="button" variant="outline" className="text-red-600 mr-auto" disabled={saving} onClick={() => save('')} data-testid="next-note-clear">Fjern</Button>
            )}
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>Avbryt</Button>
            <Button type="button" disabled={saving} onClick={() => save(value)} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="next-note-save">
              {saving ? 'Lagrer...' : 'Lagre'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default NextServiceNote;
